import classNames from 'classnames';
import {FormattedDate, FormattedTime, defineMessages, injectIntl, intlShape} from 'react-intl';
import PropTypes from 'prop-types';
import React, {useState, useEffect} from 'react';
import {connect} from 'react-redux';

import Box from '../box/box.jsx';
import Button from '../button/button.jsx';

import getSession, {requestDashApi} from '../../lib/dash-api.js';
import {setSession} from '../../reducers/dash';

import fireReactionOnIcon from './fire-reaction-on.svg';
import fireReactionOffIcon from './fire-reaction-off.svg';
import viewsIcon from './views.svg';
import forkIcon from './forks.svg';

import * as styles from './stage-footer.css';

const messages = defineMessages({
    fire: {
        defaultMessage: 'Fire Project',
        description: 'Button to trigger fire reaction',
        id: 'dash.project.fire'
    },
    unfire: {
        defaultMessage: 'Unfire Project',
        description: 'Button to trigger unfire reaction',
        id: 'dash.project.unfire'
    },
    viewsCount: {
        defaultMessage: 'Views Count',
        description: 'Count of project\'s views',
        id: 'dash.project.viewsCount'
    },
    forks: {
        defaultMessage: 'Forks',
        description: 'Button to open project\'s forks page',
        id: 'dash.project.forks'
    }
});

const StageFooter = props => {
    const [projectMetadata, setProjectMetadata] = useState(null);
    const [isFired, setIsFired] = useState(false);
    const [isDashProject, setIsDashProject] = useState(false);

    /* eslint-disable require-jsdoc, func-style */
    useEffect(() => {
        const fetchProjectMetadata = async function () {
            setIsDashProject(false);
            const res = await requestDashApi(`/projects/${props.projectId}`);
            const data = await res.json();
            if (data.ok) {
                setIsDashProject(true);
                const viewRes = await requestDashApi(`/projects/${props.projectId}/view`, {
                    method: 'POST',
                    credentials: 'include'
                });
                const viewData = await viewRes.json();
                setProjectMetadata({
                    ...data.project,
                    stats: {
                        ...data.project.stats,
                        views: viewData.views
                    }
                });
            }
        };
        fetchProjectMetadata();
    }, [props.projectId]);

    useEffect(() => {
        const fetchFireStatus = function () {
            if (!props.session?.firedProjects) return;
            setIsFired(props.session.firedProjects.includes(+props.projectId));
        };
        fetchFireStatus();
    }, [props.session?.firedProjects || []]);

    const updateSession = async function () {
        const updatedSession = await getSession();
        setSession(updatedSession);
    };

    const handleFireButtonClick = async function () {
        if (!props.session || !props.session?.id) {
            window.open('./login', '_blank');
            return;
        }
        if (isFired) {
            const res = await requestDashApi(`/projects/${props.projectId}/fire`, {
                method: 'DELETE',
                credentials: 'include'
            });
            const data = await res.json();
            if (data.ok) {
                setIsFired(false);
                setProjectMetadata(prevMetadata => ({
                    ...prevMetadata,
                    stats: {
                        ...prevMetadata.stats,
                        fires: data.fires
                    }
                }));
            }
        } else {
            const res = await requestDashApi(`/projects/${props.projectId}/fire`, {
                method: 'POST',
                credentials: 'include'
            });
            const data = await res.json();
            if (data.ok) {
                setIsFired(true);
                setProjectMetadata(prevMetadata => ({
                    ...prevMetadata,
                    stats: {
                        ...prevMetadata.stats,
                        fires: data.fires
                    }
                }));
            }
        }
        await updateSession();
    };
    /* eslint-enable require-jsdoc, func-style */

    if (!isDashProject || !props.projectId) return null;

    const fireButton = (
        <Button
            className={styles.fireButton}
            iconAlt={isFired ? props.intl.formatMessage(messages.unfire) : props.intl.formatMessage(messages.fire)}
            iconClassName={classNames(styles.fireReactionIcon, {
                [styles.fireReactionOffIcon]: !isFired
            })}
            iconSrc={isFired ? fireReactionOnIcon : fireReactionOffIcon}
            // eslint-disable-next-line react/jsx-no-bind
            onClick={handleFireButtonClick}
            title={isFired ? props.intl.formatMessage(messages.unfire) : props.intl.formatMessage(messages.fire)}
        >
            {projectMetadata?.stats?.fires || 0}
        </Button>
    );

    const viewsCount = (
        <div className={styles.viewsCount}>
            <img
                className={styles.viewsIcon}
                src={viewsIcon}
                alt={props.intl.formatMessage(messages.viewsCount)}
                draggable={false}
            />
            <span>
                {projectMetadata?.stats?.views || 0}
            </span>
        </div>
    );

    const forksButton = (
        <Button
            className={styles.forksButton}
            iconAlt={props.intl.formatMessage(messages.forks)}
            iconClassName={styles.forkIcon}
            iconSrc={forkIcon}
            // eslint-disable-next-line react/jsx-no-bind
            onClick={() => window.open(`project-forks#${props.projectId}`)}
            title={props.intl.formatMessage(messages.forks)}
        >
            {projectMetadata?.stats?.forks || 0}
        </Button>
    );

    const uploadDate = projectMetadata?.uploadedAt ? new Date(projectMetadata?.uploadedAt) : null;
    const uploadDateNode = uploadDate ? (
        <>
            <FormattedDate value={uploadDate} />
            {', '}
            <FormattedTime value={uploadDate} />
        </>
    ) : '?';

    return (
        <Box className={styles.stageFooterWrapper}>
            <div className={styles.footerButtonsRow}>
                {fireButton}
                {viewsCount}
                {forksButton}
            </div>
            <div className={styles.footerButtonsRow}>
                {uploadDateNode}
            </div>
        </Box>
    );
};

const mapStateToProps = state => ({
    projectId: state.scratchGui.projectState.projectId,
    session: state.scratchGui.dash.session
});

StageFooter.propTypes = {
    intl: intlShape,
    projectId: PropTypes.string,
    session: PropTypes.object
};

export default injectIntl(connect(
    mapStateToProps
)(StageFooter));
